"use client"

import React, { RefObject, useRef, useMemo, useState, useCallback } from "react"
import { Box, HStack } from "@chakra-ui/react"
import styles from "./styles.module.css"
import classNames from "classnames"
import { Link } from '@/i18n/navigation'
import { useLocale } from 'next-intl'
import { useScrollPosition } from "./useScrollPosition"
import { useCategoryOffsets } from "./useCategoryOffsets"
import { useActiveCategory } from "./useActiveCategory"
import { Button } from "@/shared/ui/button"
import { ICategoryWithProducts } from '@/features/brand-menu'
import { useCustomLocale } from '@/_hooks/useCustomLocale'
import { categoryHref, isModifiedClick } from '../category-menu-link'

interface IProps {
  data: ICategoryWithProducts[]
  instanceRef: RefObject<any>
  isInteractive?: boolean
  isSeparatePage?: boolean
  activeSlug?: string
}

function FixedBar({
  isInteractive = false,
  data,
  instanceRef,
  isSeparatePage,
  activeSlug,
}: IProps) {
  const [isManualScroll, setIsManualScroll] = useState(false)
  const currentLocale = useCustomLocale()
  const locale = useLocale()

  const catalogMenuRef = useRef<HTMLDivElement>(null)
  const buttonRefs = useRef<Map<string, HTMLElement>>(new Map())

  // Get category offsets
  const categoriesOffsetTops = useCategoryOffsets(data)

  // Handle scroll position
  const scrollPosition = useScrollPosition({
    isManualScroll,
    onScrollChange: () => { }, // No additional action needed
    onManualScrollEnd: () => setIsManualScroll(false),
  })

  // Handle active category and fixed catalog state
  const { activeCategory, isFixedCatalog, handleCategoryClick } =
    useActiveCategory({
      data,
      categoriesOffsetTops,
      scrollPosition,
      isManualScroll,
      instanceRef,
    })

  const resolvedActiveCategory = isSeparatePage && activeSlug
    ? activeSlug
    : activeCategory

  const headerOffsetMobile = useMemo(() => {
    return isInteractive ? 0 : "0px"
  }, [isInteractive])

  // Handle horizontal scroll of the catalog menu when activeCategory changes
  const scrollToActiveCategory = useCallback(() => {
    if (
      !catalogMenuRef.current ||
      !resolvedActiveCategory ||
      !buttonRefs.current.has(resolvedActiveCategory)
    )
      return

    const targetElement = buttonRefs.current.get(resolvedActiveCategory)
    if (!targetElement) return

    const containerWidth = catalogMenuRef.current.offsetWidth
    const elementWidth = targetElement.offsetWidth
    const elementLeft = targetElement.offsetLeft
    const scrollLeft = elementLeft - containerWidth / 2 + elementWidth / 2

    catalogMenuRef.current.scrollTo({
      left: scrollLeft,
      behavior: "smooth",
    })
  }, [resolvedActiveCategory])

  // Trigger scroll when active category changes
  React.useEffect(() => {
    scrollToActiveCategory()
  }, [scrollToActiveCategory])

  // Handle manual category click with manual scroll state
  const handleCategoryClickWithScroll = useCallback(
    (slug: string, event: React.MouseEvent) => {
      if (isModifiedClick(event)) return
      handleCategoryClick(slug, event)
      setIsManualScroll(true)
    },
    [handleCategoryClick]
  )

  const categoryButtons = useMemo(() => {
    return data?.map((category, idx) => {
      // For separate pages, use Next.js Link
      if (isSeparatePage) {
        return (
          <Button
            key={`${category.id}-${category.slug}-${idx}`}
            ref={(el) => {
              if (el) buttonRefs.current.set(category.slug, el)
            }}
            variant="ghost"
            size={{ base: "xs", lg: "sm" }}
            color={category.slug === resolvedActiveCategory ? "text" : "text.muted"}
            bg={
              category.slug === resolvedActiveCategory
                ? "background"
                : "background.card"
            }
            aria-current={category.slug === resolvedActiveCategory ? "true" : undefined}
            asChild
          >
            <Link href={`/categories/${category?.slug}`}>
              {category.title?.[currentLocale]}
            </Link>
          </Button>
        )
      }

      return (
        <Button
          key={`${category.id}-${category.slug}-${idx}`}
          ref={(el) => {
            if (el) buttonRefs.current.set(category.slug, el)
          }}
          variant="ghost"
          size={{ base: "xs", lg: "sm" }}
          color={category.slug === resolvedActiveCategory ? "text" : "text.muted"}
          bg={
            category.slug === resolvedActiveCategory ? "background" : "background.card"
          }
          aria-current={category.slug === resolvedActiveCategory ? "true" : undefined}
          asChild
        >
          <a
            href={categoryHref(locale, category.slug)}
            onClick={(e) => handleCategoryClickWithScroll(category.slug, e)}
          >
            {category.title?.[currentLocale]}
          </a>
        </Button>
      )
    })
  }, [
    data,
    resolvedActiveCategory,
    currentLocale,
    locale,
    isSeparatePage,
    handleCategoryClickWithScroll,
  ])

  return (
    <Box
      bg="background.card"
      className={classNames(styles.top, {
        [styles.active]: isFixedCatalog,
      })}
      transform={{
        base: `translateY(${isFixedCatalog ? headerOffsetMobile : "-100%"})`,
        lg: `translateY(${isFixedCatalog ? (isInteractive ? 0 : "150%") : "-100%"
          })`,
      }}
    >
      <Box pt={{ base: 2, lg: 3 }} pb={3}>
        <HStack
          gap={0}
          px={4}
          overflowX="auto"
          scrollbar="hidden"
          ref={catalogMenuRef}
          scrollBehavior="smooth"
          height={{ base: "fit-content" }}
        >
          {categoryButtons}
        </HStack>
      </Box>
    </Box>
  )
}
export default React.memo(FixedBar)
